import React, { useState } from "react";
import { Column } from 'primereact/column';
import IApiResponseGetDevicesParam from "../../interfaces/IApiResponseGetDevicesParam";
import DeviceParamDialog from "./dialog";
import { useToast } from "../../context/ToastContext";
import DeviceParamApi from "../../services/DeviceParamApi";
import { useTranslation } from 'react-i18next';

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import DeviceApi from "../../services/DeviceApi";
import { Device } from "../../components/Filters/types/types";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const RegisterDeviceParam: React.FC = () => {
    //#region Set States
    const [devicesParam] = useState<IApiResponseGetDevicesParam[]>([]);
    const [filteredDevicesParam, setFilteredDevicesParam] = useState<IApiResponseGetDevicesParam[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [paramKey, setParamKey] = useState<string | null>(null);
    const [paramValor, setParamValor] = useState<string | null>(null);
    const [description, setdescription] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedDeviceParam, setSelectedDeviceParam] = useState<IApiResponseGetDevicesParam | null>(null);
    const [selectedOption, setSelectedOption] = useState<boolean | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [paramToDelete, setParamToDelete] = useState<string | null>(null);
    const { displayToast } = useToast();
    const { t: translateMessage } = useTranslation("messages");
    const { t: translateForm } = useTranslation("form");
    const { t: translateGeneral } = useTranslation("general");

    const [devices, setDevices] = useState<Device[]>([]);

    // Pagination/Sort state
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortField, setSortField] = useState<string>("paramKey");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    const handleEditClick = (station: IApiResponseGetDevicesParam): void => {
        setSelectedDeviceParam(station);
        setEditMode(true);
        setVisible(true);
      };

    const handleDialogSubmit = async (data: IApiResponseGetDevicesParam): Promise<void> => {
        if (editMode && selectedDeviceParam) {

            await updateDeviceParam(data.id,data.deviceId, data.paramValue, data.paramKey, data.description,data.active);
        } else {

            await createDeviceParam(data.deviceId, data.paramValue, data.paramKey, data.description,data.active);
        }
        setVisible(false);
        setSelectedDeviceParam(null);
        setEditMode(false);
    };
    //#endregion

    //#region Filters
    const resetFilters = (): void => {
        setSelectedDeviceId(null);
        setDeviceId(null);
        setParamKey(null);
        setParamValor(null);
        setdescription(null);
        setSelectedOption(null);
        filterDeviceParam();
    };

    const createDeviceParam = async (deviceId: string, paramValue:string,paramKey:string,description: string,active:boolean): Promise<void> => {
        try {
            const response = await DeviceParamApi.CreateDeviceParam({deviceId,paramValue,paramKey,description,active} );
            await filterDeviceParam();
            if (response.data) {

                displayToast(
                  "success",
                  translateMessage("registerSuccess"),
                    "",
                  false,
                  3000
              );
            }
        } catch (error) {
            console.error('Failed: ', error);
            displayToast(
                "error",
                `${translateMessage("error")}: ${error}`,
                "",
                false,
                3000
            );

        }
      };

    const updateDeviceParam = async (id:string,deviceId: string, paramValue:string,paramKey:string,description: string,active:boolean): Promise<void> => {
        try {
            const response = await DeviceParamApi.UpdateDeviceParam({id, deviceId,paramValue,paramKey,description,active});
            await filterDeviceParam();
            if (response.statusCode === 200) {

                displayToast(
                  "success",
                   translateMessage("editSuccess"),
                    "",
                  false,
                  3000
              );
            }
        } catch (error) {
            console.error('Failed: ', error);
            displayToast(
                "error",
                `${translateMessage("error")}: ${error}`,
                "",
                false,
                3000
            );

        }
      };

      const deleteDeviceParam = async (id: string): Promise<void> => {
        try {
            await DeviceParamApi.DeleteDeviceParam(id);
            await filterDeviceParam();
            displayToast(
                "success",
                translateMessage("deleteSuccess"),
                "",
                false,
                3000
            );
        } catch (error) {
            console.error('Failed to delete station: ', error);
            displayToast(
                "error",
                `${translateMessage("error")}: ${error}`,
                "",
                false,
                3000
            );
        }

    }

    const filterDeviceParam = async (): Promise<void> => {
        setIsLoading(true);
        let filtered = devicesParam;

        try {
            if (selectedDeviceId || deviceId || paramKey || paramValor || description || selectedOption !== null) {
                if (selectedDeviceId) {
                    const response = await DeviceParamApi.GetDeviceParamsByDeviceId(selectedDeviceId || deviceId!);
                    filtered = response.data;
                }

                if (paramKey) {
                    filtered = filtered.filter(device =>
                        device.paramKey && device.paramKey.toLowerCase().includes(paramKey.toLowerCase())
                    );
                }

                if (paramValor) {
                    filtered = filtered.filter(device =>
                        device.paramValue && device.paramValue.toLowerCase().includes(paramValor.toLowerCase())
                    );
                }

                if (description) {
                    filtered = filtered.filter(device =>
                        device.description && device.description.toLowerCase().includes(description.toLowerCase())
                    );
                }

                if (selectedOption !== null) {
                    filtered = filtered.filter(device => device.active === selectedOption);
                }
            }

        } catch (error) {
            console.error(translateMessage("errorSearch"),  error);
        }

        setFilteredDevicesParam(filtered);
        setIsLoading(false);
    };


    const fetchAllDevices = async (): Promise<void> => {
        try {
            const response = await DeviceApi.GetAllDevices();
            if (response.data && Array.isArray(response.data)) {
                setDevices(response.data);
            }
        } catch (error) {
            console.error('Error fetching devices:', error);
        }
    };

    React.useEffect(() => {
        fetchAllDevices();
    }, []);

    const handlePageChange = (e: PaginatorPageChangeEvent): void => {
        setPage(e.first);
        setRows(e.rows);
    };

    const handleSortChange = (e: any): void => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
    };
    //#endregion

    //#region Table View
    return (
        <div className="overflow-hidden">
            <PageHeader
                title={translateForm("deviceParams")}
                description="Gerencie os parâmetros de configuração dos dispositivos cadastrados"
            >
                <Button
                    onClick={() => {
                        setSelectedDeviceParam(null);
                        setEditMode(false);
                        setVisible(true);
                    }}
                    label={translateGeneral("register")}
                    icon="add"
                    severity="primary"
                />
            </PageHeader>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <FilterPanel
                    title="Filtros"
                    onApply={filterDeviceParam}
                    onClear={resetFilters}
                >
                    <Input
                        type="select"
                        label={translateForm("devices")}
                        value={selectedDeviceId || ""}
                        options={devices.map(d => ({ label: d.name, value: d.id, id: d.id }))}
                        onChange={(val: any) => setSelectedDeviceId(val)}
                    />
                    <Input
                        type="text"
                        label={translateForm("paramKey")}
                        value={paramKey || ""}
                        onChange={(val: any) => setParamKey(val)}
                    />
                    <Input
                        type="text"
                        label={translateForm("paramValue")}
                        value={paramValor || ""}
                        onChange={(val: any) => setParamValor(val)}
                    />
                    <Input
                        type="text"
                        label={translateForm("description")}
                        value={description || ""}
                        onChange={(val: any) => setdescription(val)}
                    />
                    <Input
                        type="select"
                        label={translateForm("active")}
                        value={selectedOption === null ? "" : selectedOption.toString()}
                        options={[
                            { label: translateGeneral("all"), value: "", id: "all" },
                            { label: "OK", value: "true", id: "ok" },
                            { label: "NO OK", value: "false", id: "no-ok" }
                        ]}
                        onChange={(val: any) => {
                            if (val === "") setSelectedOption(null);
                            else setSelectedOption(val === "true");
                        }}
                    />
                </FilterPanel>

                {isLoading ? (
                    <div>{translateGeneral("loading")}</div>
                ) : (
                    <DataTable
                        value={filteredDevicesParam}
                        paginated
                        first={page}
                        rows={rows}
                        totalRecords={filteredDevicesParam.length}
                        onPageChange={handlePageChange}
                        rowsPerPageOptions={[10, 20, 50]}
                        loading={isLoading}
                        sortField={sortField}
                        sortOrder={sortOrder === "asc" ? 1 : -1}
                        onSort={handleSortChange}
                        emptyMessage={translateGeneral("noRecordsFound")}
                    >
                        <Column
                            field="paramKey"
                            header={translateForm("paramKey")}
                            sortable
                        />
                        <Column
                            field="paramValue"
                            header={translateForm("paramValue")}
                            sortable
                        />
                        <Column
                            field="description"
                            header={translateForm("description")}
                            sortable
                        />
                        <Column
                            field="active"
                            header={translateForm("active")}
                            body={(rowData: IApiResponseGetDevicesParam) => (rowData.active ? "OK" : "NO OK")}
                            sortable
                        />
                        <Column
                            field="actions"
                            header={translateGeneral("actions")}
                            body={(rowData: IApiResponseGetDevicesParam) => (
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                    <IconButton
                                        icon="edit"
                                        variant="text"
                                        severity="secondary"
                                        onClick={() => handleEditClick(rowData)}
                                        aria-label={translateGeneral("edit")}
                                    />
                                    <IconButton
                                        icon="delete"
                                        variant="text"
                                        severity="secondary"
                                        onClick={() => {
                                            setParamToDelete(rowData.id);
                                            setShowDeleteConfirm(true);
                                        }}
                                        aria-label={translateGeneral("delete")}
                                    />
                                </div>
                            )}
                            style={{ width: "8rem" }}
                        />
                    </DataTable>
                )}
            </div>
            <DeviceParamDialog
                visible={visible}
                onHide={() => setVisible(false)}
                onCreate={handleDialogSubmit}
                device={selectedDeviceParam}
            />
            <ConfirmModal
                visible={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={() => {
                    if (paramToDelete) deleteDeviceParam(paramToDelete);
                    setShowDeleteConfirm(false);
                }}
                title={translateGeneral("confirmation")}
                message={translateMessage("deleteDeviceParam")}
            />
        </div>
    );
    //#endregion
};

export default RegisterDeviceParam;
